import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';

export const useEnabledCalendars = () => {
  const { isAuthenticated, token, loading: authLoading } = useAuth();
  const [calendarData, setCalendarData] = useState({
    enabledCalendars: [],
    loading: true,
    error: null,
    lastFetch: null
  });

  const fetchEnabledCalendars = useCallback(async () => {
    if (!isAuthenticated || !token) {
      setCalendarData({
        enabledCalendars: [],
        loading: false,
        error: null,
        lastFetch: null
      });
      return [];
    }

    try {
      setCalendarData(prev => ({ ...prev, loading: true, error: null }));

      let headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };

      // Only fetch enabled calendar preferences, not the full calendar list
      let response = await fetch('/api/google/calendars/enabled', { method: 'GET', headers });

      let data = await response.json();

      if (!response.ok) {
        // If token expired, retry once with a refreshed token
        if (response.status === 401) {
          headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };
          response = await fetch('/api/google/calendars/enabled', { method: 'GET', headers });
          data = await response.json();
          if (response.ok) {
            const enabledCalendars = data.enabledCalendars || [];
            setCalendarData({ enabledCalendars, loading: false, error: null, lastFetch: new Date() });
            return enabledCalendars;
          }
        }

        // Handle specific API error responses
        if (response.status === 401 && (data.requiresAuth || (data.error && data.error.includes('not connected')))) {
          setCalendarData({
            enabledCalendars: [],
            loading: false,
            error: 'google_calendar_not_connected',
            lastFetch: new Date()
          });
          return [];
        }
        
        setCalendarData({
          enabledCalendars: [],
          loading: false,
          error: 'google_calendar_not_connected',
          lastFetch: new Date()
        });
        return [];
      }

      const enabledCalendars = data.enabledCalendars || [];
      
      setCalendarData({
        enabledCalendars,
        loading: false,
        error: null,
        lastFetch: new Date()
      });

      return enabledCalendars;
    } catch (error) {
      console.error('Error fetching enabled calendars (handled gracefully):', error);
      
      setCalendarData({
        enabledCalendars: [],
        loading: false,
        error: 'google_calendar_not_connected',
        lastFetch: new Date()
      });
      
      return [];
    }
  }, [isAuthenticated, token]);

  // Automatically fetch enabled calendars when authenticated
  useEffect(() => {
    if (!authLoading && isAuthenticated && token) {
      fetchEnabledCalendars().catch(error => {
        console.error('Error in useEffect fetchEnabledCalendars (handled):', error);
      });
    }
  }, [isAuthenticated, token, authLoading, fetchEnabledCalendars]);

  return {
    ...calendarData,
    refreshEnabledCalendars: fetchEnabledCalendars,
    hasEnabledCalendars: calendarData.enabledCalendars.length > 0
  };
};